import { Fragment, useState } from "react";
import "./OrganizationPhotos.css";
import {
  ORGANIZATION_ALBUMS,
  ORGANIZATION_EVENTS,
  ORGANIZATION_PHOTOS,
} from "../../api/organization";
import { SPORTS } from "../../api/sport";
import Album from "../Album";
import PhotosCard from "../PhotosCard";
import AlbumsCard from "../AlbumsCard";
import FilterInput from "../FilterInput";
import Chip from "../Chip";
import { ReactComponent as TimesIcon } from "../../icons/times.svg";
import { gql } from "@apollo/client";

function DirectoryFilter({
  placeholder,
  query,
  items,
  addFilter,
  removeFilter,
  defaultValue,
  refetch,
  appStateKey,
  vars,
}) {
  const [value, setValue] = useState(defaultValue || []);

  return (
    <div className="filter-group">
      {/*<div className="filter-group-title">Sport</div>*/}
      <div className="filter-group-input">
        <FilterInput
          placeholder={placeholder}
          query={query}
          items={items}
          onItemSelect={(item) => {
            const newValue = [...value, item];
            setValue(newValue);
            addFilter(newValue);
            // setAppState({ [appStateKey]: newValue });
          }}
          variables={vars}
        />
      </div>
      <div className="filter-group-applied">
        {value.map((s) => (
          <Chip
            onClick={() => {
              const newValue = value.filter((x) => x.id !== s.id);
              setValue(newValue);
              removeFilter(newValue);
              // setAppState({ [appStateKey]: newValue });
            }}
          >
            {s.name} <TimesIcon style={{ stroke: "white", height: 12 }} />
          </Chip>
        ))}
      </div>
    </div>
  );
}

function OrganizationPhotos({ id, organization, canManagePage }) {
  const [selectedAlbum, setSelectedAlbum] = useState("");
  const [sportsFilter, setSportsFilter] = useState([]);
  const [teamsFilter] = useState([]);
  const [eventsFilter, setEventsFilter] = useState([]);

  return (
    <div className="organization-photos">
      <div className="left-column">
        {selectedAlbum ? (
          <Album
            id={selectedAlbum}
            dismiss={() => {
              setSelectedAlbum("");
            }}
            onDelete={(cache, deleteMediaSet) => {
              console.log("Reached on delete");
              setSelectedAlbum("");
              cache.modify({
                id: cache.identify(organization),
                fields: {
                  mediaSets(existingMediaRefs, { readField }) {
                    return existingMediaRefs.filter(
                      (mediaRef) =>
                        deleteMediaSet.id !== readField("id", mediaRef)
                    );
                  },

                  albumsCount(current) {
                    return current - 1;
                  },
                },
              });
            }}
          />
        ) : (
          <Fragment>
            <AlbumsCard
              query={ORGANIZATION_ALBUMS}
              vars={{ id, take: 2 }}
              getAlbums={(data) => data?.organization?.mediaSets || []}
              totalAlbums={(data) => data?.organization?.albumsCount || 0}
              noData={`No albums tagged with ${organization.name} yet.`}
              setSelectedAlbum={setSelectedAlbum}
              filters={{ sportsFilter, teamsFilter, eventsFilter }}
              processFilters={(filters) => {
                return {
                  filters: {
                    sports: sportsFilter,
                    teams: teamsFilter,
                    events: eventsFilter,
                  },
                };
              }}
              addAlbum={
                canManagePage
                  ? {
                      sourceId: id,
                      sourceType: "organization",
                      onAddAlbum: (cache, { data: { createMediaSet } }) => {
                        cache.modify({
                          id: cache.identify(organization),
                          fields: {
                            mediaSets(existingMediaRefs, { readField }) {
                              const newMediasetRef = cache.writeFragment({
                                data: createMediaSet,
                                fragment: gql`
                                  fragment NewMediaSet on MediaSet {
                                    id
                                    name
                                    preview {
                                      id
                                      filename
                                    }
                                    source {
                                      id
                                      fixture {
                                        id
                                        scheduledStart
                                        roles(
                                          where: {
                                            type: { name: "PARTICIPANT" }
                                          }
                                        ) {
                                          id
                                          organization {
                                            id
                                            name
                                          }
                                          profile {
                                            id
                                            name
                                          }
                                        }
                                      }
                                    }
                                  }
                                `,
                              });

                              // Quick safety check - if the new comment is already
                              // present in the cache, we don't need to add it again.
                              if (
                                existingMediaRefs.some(
                                  (ref) =>
                                    readField("id", ref) === createMediaSet.id
                                )
                              ) {
                                return existingMediaRefs;
                              }

                              return [newMediasetRef, ...existingMediaRefs];

                              // const newMediaSets = [
                              //   createMediaSet,
                              //   ...existingMediaRefs,
                              // ];
                              // console.log("new Media Sets:", newMediaSets);
                              // return newMediaSets;
                            },
                          },
                        });
                      },
                    }
                  : false
              }
            />

            <PhotosCard
              query={ORGANIZATION_PHOTOS}
              vars={{ id, take: 16 }}
              getPhotos={(data) => data?.organization?.media || []}
              totalPhotos={(data) => data?.organization?.photosCount || 0}
              noData={`No tagged photos of ${organization.name} yet.`}
              filters={{ sportsFilter, teamsFilter, eventsFilter }}
              processFilters={(filters) => {
                return {
                  filters: {
                    sports: sportsFilter,
                    teams: teamsFilter,
                    events: eventsFilter,
                  },
                };
              }}
              onDelete={(cache, { data: { deleteMedia } }) => {
                cache.modify({
                  id: cache.identify(organization),
                  fields: {
                    media(existingMediaRefs, { readField }) {
                      return existingMediaRefs.filter(
                        (mediaRef) =>
                          deleteMedia.id !== readField("id", mediaRef)
                      );
                    },
                  },
                });
              }}
              addPhoto={
                canManagePage
                  ? {
                      sourceId: id,
                      sourceType: "organization",
                      onAddPhoto: (cache, createMedia) => {
                        cache.modify({
                          id: cache.identify(organization),
                          fields: {
                            media(existingMediaRefs, { readField }) {
                              const newMediaRef = cache.writeFragment({
                                data: createMedia.media,
                                fragment: gql`
                                  fragment NewMedia on Media {
                                    id
                                    createdAt
                                    type
                                    subtype
                                    isExternal
                                    filename
                                    public
                                    aspectRatio
                                    canManageMedia
                                  }
                                `,
                              });

                              // Quick safety check - if the new comment is already
                              // present in the cache, we don't need to add it again.
                              if (
                                existingMediaRefs.some(
                                  (ref) =>
                                    readField("id", ref) ===
                                    createMedia.media.id
                                )
                              ) {
                                return existingMediaRefs;
                              }

                              return [newMediaRef, ...existingMediaRefs];

                              // const newMediaSets = [
                              //   createMediaSet,
                              //   ...existingMediaRefs,
                              // ];
                              // console.log("new Media Sets:", newMediaSets);
                              // return newMediaSets;
                            },
                          },
                        });
                        // setAppState({ modal: false });
                      },
                    }
                  : false
              }
            />
          </Fragment>
        )}
      </div>

      <div className="right-column">
        <div className="card-section">
          <div className="card-section-title">
            Filters<div className="reset">Reset</div>
          </div>

          <DirectoryFilter
            placeholder={"Enter a sport"}
            query={SPORTS}
            items={(d) => {
              return d?.sports;
            }}
            addFilter={(sports) => {
              setSportsFilter(sports.map((s) => s.name));
            }}
            removeFilter={(sports) => {
              setSportsFilter(sports.map((s) => s.name));
            }}
            defaultValue={sportsFilter}
            appStateKey={"sportsFilter"}
          />

          {/* <DirectoryFilter
            placeholder={"Enter a team"}
            query={ORGANIZATION_EVENT_PARTICIPANTS}
            vars={{ id }}
            items={(d) => {
              return d?.organization?.eventParticipants || [];
            }}
            addFilter={(orgs) => {
              setTeamsFilter(orgs.map((s) => s.id));
            }}
            removeFilter={(orgs) => {
              setTeamsFilter(orgs.map((s) => s.id));
            }}
            defaultValue={teamsFilter}
            appStateKey={"teamsFilter"}
          /> */}

          <DirectoryFilter
            placeholder={"Enter an event"}
            query={ORGANIZATION_EVENTS}
            vars={{ id }}
            items={(d) => {
              return d?.organization?.events;
            }}
            addFilter={(events) => {
              setEventsFilter(events.map((s) => s.id));
            }}
            removeFilter={(events) => {
              setEventsFilter(events.map((s) => s.id));
            }}
            defaultValue={eventsFilter}
            appStateKey={"eventsFilter"}
          />
        </div>
      </div>
    </div>
  );
}

export default OrganizationPhotos;
